export const PAID_READING_POLL_INTERVAL_MS = 2_000;
export const PAID_READING_POLL_WINDOW_MS = 180_000;

export interface PaidPollingResponse<TPaid> {
  session: { status: string; current_step: string };
  paid_reading?: TPaid | null;
  purchase?: { payment_status: string; fulfillment_status: string } | null;
}

export type PaidPollingClassification<TPaid, TResponse extends PaidPollingResponse<TPaid>> =
  | { kind: 'ready'; reading: TPaid; response: TResponse }
  | { kind: 'review_required'; response: TResponse }
  | { kind: 'retryable_failure'; response: TResponse }
  | { kind: 'pending'; response: TResponse };

export type PaidPollingResult<TPaid, TResponse extends PaidPollingResponse<TPaid>> =
  | PaidPollingClassification<TPaid, TResponse>
  | { kind: 'timeout'; response?: TResponse }
  | { kind: 'cancelled' };

interface PaidPollingOptions {
  startedAt?: number;
  windowMs?: number;
  intervalMs?: number;
  now?: () => number;
  wait?: (milliseconds: number) => Promise<void>;
  signal?: AbortSignal;
}

export function paidPollingDeadline(startedAt: number, windowMs = PAID_READING_POLL_WINDOW_MS): number {
  return startedAt + windowMs;
}

export function classifyPaidPollingResponse<TPaid, TResponse extends PaidPollingResponse<TPaid>>(response: TResponse): PaidPollingClassification<TPaid, TResponse> {
  if (response.paid_reading) return { kind: 'ready', reading: response.paid_reading, response };
  if (response.purchase?.fulfillment_status === 'review_required') return { kind: 'review_required', response };
  if (response.purchase?.fulfillment_status === 'retryable_failure' || response.session.status === 'generation_failed') return { kind: 'retryable_failure', response };
  return { kind: 'pending', response };
}

function defaultWait(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) { resolve(); return; }
    const timer = window.setTimeout(done, milliseconds);
    function done(): void {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    }
    signal?.addEventListener('abort', done, { once: true });
  });
}

export async function pollForPaidReading<TPaid, TResponse extends PaidPollingResponse<TPaid>>(
  fetchFresh: () => Promise<TResponse>,
  options: PaidPollingOptions = {},
): Promise<PaidPollingResult<TPaid, TResponse>> {
  const now = options.now ?? Date.now;
  const startedAt = options.startedAt ?? now();
  const deadline = paidPollingDeadline(startedAt, options.windowMs);
  const intervalMs = options.intervalMs ?? PAID_READING_POLL_INTERVAL_MS;
  const wait = options.wait ?? ((milliseconds: number) => defaultWait(milliseconds, options.signal));
  let lastResponse: TResponse | undefined;
  let firstRequest = true;

  const finalFreshCheck = async (): Promise<PaidPollingResult<TPaid, TResponse>> => {
    if (options.signal?.aborted) return { kind: 'cancelled' };
    try {
      const response = await fetchFresh();
      if (options.signal?.aborted) return { kind: 'cancelled' };
      const classification = classifyPaidPollingResponse<TPaid, TResponse>(response);
      return classification.kind === 'pending' ? { kind: 'timeout', response } : classification;
    } catch {
      return options.signal?.aborted ? { kind: 'cancelled' } : { kind: 'timeout', ...(lastResponse ? { response: lastResponse } : {}) };
    }
  };

  while (!options.signal?.aborted) {
    if (!firstRequest && now() >= deadline) return finalFreshCheck();
    try {
      const response = await fetchFresh();
      if (options.signal?.aborted) return { kind: 'cancelled' };
      lastResponse = response;
      const classification = classifyPaidPollingResponse<TPaid, TResponse>(response);
      if (classification.kind !== 'pending') return classification;
    } catch {
      if (options.signal?.aborted) return { kind: 'cancelled' };
    }
    firstRequest = false;
    if (now() >= deadline) return finalFreshCheck();
    await wait(Math.min(intervalMs, Math.max(0, deadline - now())));
  }
  return { kind: 'cancelled' };
}
